import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, Quote, PenLine, Loader2, Send, CheckCircle2 } from "lucide-react";
import apiClient from "@/lib/api";
import { toast } from "sonner";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Testimonials() {
  const [reviews, setReviews] = useState([]);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    apiClient
      .get("/reviews")
      .then(({ data }) => setReviews(data))
      .catch(() => {});
  }, []);

  return (
    <section id="reviews" data-testid="testimonials-section" className="lk-section px-6 md:px-12 lg:px-24 bg-[#050505] border-t border-[#D4AF37]/10">
      <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Kind Words</span>
          <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 leading-tight">
            Crowned by
            <br />
            <span className="italic gold-gradient-text">our clients.</span>
          </h2>
        </div>
        <button
          onClick={() => setFormOpen((v) => !v)}
          data-testid="share-review-btn"
          className="lk-btn-secondary text-xs inline-flex items-center gap-2"
        >
          <PenLine className="w-4 h-4" /> {formOpen ? "Close" : "Share Your Experience"}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {formOpen && (
          <motion.div
            key="review-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden mb-14"
          >
            <ReviewForm />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {reviews.map((r, i) => (
          <motion.figure
            key={r.id}
            className="lk-card p-8 flex flex-col"
            data-testid={`review-card-${i}`}
            custom={i % 3}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
          >
            <Quote className="w-6 h-6 text-[#D4AF37]/40 mb-4" />
            <Stars rating={r.rating} />
            <blockquote className="text-zinc-300 leading-relaxed text-sm mt-4 flex-1">
              "{r.text}"
            </blockquote>
            <figcaption className="mt-6 pt-5 border-t border-[#D4AF37]/10">
              <div className="font-serif text-lg text-white">{r.name}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mt-1">{r.event_type}</div>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </section>
  );
}

function Stars({ rating, onSelect, testPrefix }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) =>
        onSelect ? (
          <button
            key={n}
            type="button"
            onClick={() => onSelect(n)}
            data-testid={`${testPrefix}-${n}`}
            className="p-0.5 transition-transform hover:scale-125"
          >
            <Star className={`w-5 h-5 ${n <= rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-zinc-700"}`} />
          </button>
        ) : (
          <Star key={n} className={`w-4 h-4 ${n <= rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-zinc-700"}`} />
        )
      )}
    </div>
  );
}

function ReviewForm() {
  const [form, setForm] = useState({ name: "", event_type: "", rating: 5, text: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await apiClient.post("/reviews", form);
      setSent(true);
      toast.success("Thank you! Your review is pending approval.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit your review. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="lk-card p-10 text-center" data-testid="review-success">
        <CheckCircle2 className="w-10 h-10 text-[#D4AF37] mx-auto mb-4" />
        <h3 className="font-serif text-2xl mb-2">Thank you, {form.name.split(" ")[0]}.</h3>
        <p className="text-sm text-zinc-400">Your review has been submitted and will appear once approved.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="lk-card p-8 grid md:grid-cols-2 gap-x-8 gap-y-6" data-testid="review-form">
      <div>
        <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2 block">Your name</label>
        <input
          type="text" required minLength={2} value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className="lk-input" placeholder="e.g. Jasmine R." data-testid="review-name-input"
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2 block">Your event</label>
        <input
          type="text" required minLength={2} value={form.event_type}
          onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value }))}
          className="lk-input" placeholder="e.g. Wedding Reception" data-testid="review-event-input"
        />
      </div>
      <div className="md:col-span-2">
        <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2 block">Your rating</label>
        <Stars rating={form.rating} onSelect={(n) => setForm((f) => ({ ...f, rating: n }))} testPrefix="review-rating" />
      </div>
      <div className="md:col-span-2">
        <label className="text-xs uppercase tracking-widest text-[#D4AF37] mb-2 block">Your experience</label>
        <textarea
          required minLength={10} maxLength={1000} rows={4} value={form.text}
          onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))}
          className="lk-input resize-none" placeholder="Tell us about your event…" data-testid="review-text-input"
        />
      </div>
      <div className="md:col-span-2 flex justify-end">
        <button type="submit" disabled={sending} className="lk-btn-primary text-xs inline-flex items-center gap-2 disabled:opacity-50" data-testid="review-submit">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Submit Review
        </button>
      </div>
    </form>
  );
}
